import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Bold, Italic, RemoveFormatting, Underline } from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { stripHtml } from '../utils/answerValidation';

export function RichTextAnswer({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [savedAt, setSavedAt] = useState('');
  const plainText = useMemo(() => stripHtml(value), [value]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const sync = () => {
    const nextValue = sanitizeHtml(editorRef.current?.innerHTML ?? '');
    onChange(nextValue);
    setSavedAt(new Date().toLocaleTimeString());
  };

  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/55">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 p-2" role="toolbar" aria-label="Long answer formatting">
        <ToolbarButton label="Bold" command="bold" editorRef={editorRef} icon={<Bold className="h-4 w-4" />} />
        <ToolbarButton label="Italic" command="italic" editorRef={editorRef} icon={<Italic className="h-4 w-4" />} />
        <ToolbarButton label="Underline" command="underline" editorRef={editorRef} icon={<Underline className="h-4 w-4" />} />
        <ToolbarButton label="Clear formatting" command="removeFormat" editorRef={editorRef} icon={<RemoveFormatting className="h-4 w-4" />} />
        <span className="ml-auto text-xs text-slate-400">Autosaved {savedAt || 'pending'}</span>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className="min-h-64 w-full rounded-b-lg p-4 text-sm leading-7 text-slate-100 outline-none focus:ring-2 focus:ring-neon-blue"
        onInput={sync}
        onBlur={sync}
        role="textbox"
        aria-multiline="true"
        suppressContentEditableWarning
      />
      <div className="flex flex-wrap gap-4 border-t border-white/10 px-4 py-2 text-xs text-slate-400">
        <span>Word count: {plainText ? plainText.split(/\s+/).length : 0}</span>
        <span>Character count: {plainText.length}</span>
      </div>
    </div>
  );
}

function ToolbarButton({ label, command, editorRef, icon }: { label: string; command: string; editorRef: RefObject<HTMLDivElement>; icon: JSX.Element }) {
  return (
    <button
      type="button"
      className="focus-ring grid h-9 w-9 place-items-center rounded-md border border-white/10 bg-white/[0.08] text-slate-100 hover:border-neon-violet"
      aria-label={label}
      title={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        editorRef.current?.focus();
        document.execCommand(command);
      }}
    >
      {icon}
    </button>
  );
}
